import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

const STATUSES = ['Active', 'Suspended', 'Inactive'];

const PERMISSION_FIELDS = [
  { key: 'customer_read', label: 'Customer Module — Read' },
  { key: 'customer_write', label: 'Customer Module — Write' },
  { key: 'operations_read', label: 'Operations Module — Read' },
  { key: 'operations_write', label: 'Operations Module — Write' },
  { key: 'billing_read', label: 'Billing Module — Read' },
  { key: 'billing_write', label: 'Billing Module — Write' },
  { key: 'administration_access', label: 'Administration Access' },
];

const EMPTY_ACCOUNT = { id: null, name: '', email: '', status: 'Active', roleIds: [], tugId: '' };

function statusBadge(status) {
  if (status === 'Active') return 'bg-success';
  if (status === 'Suspended') return 'bg-warning text-dark';
  return 'bg-secondary';
}

export default function UserAccountsRbacPage() {
  const [accounts, setAccounts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tugs, setTugs] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Client-side filter for the loaded account registry.
  const [accountQuery, setAccountQuery] = useState('');

  // Manage User Account form state.
  const [form, setForm] = useState(EMPTY_ACCOUNT);
  const [formErrors, setFormErrors] = useState({});
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountMsg, setAccountMsg] = useState(null); // { type, text }

  // Security Policy Matrix editor state.
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [policy, setPolicy] = useState({});
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyMsg, setPolicyMsg] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [accountsRes, rolesRes, matrixRes] = await Promise.all([
        api.listAccounts(),
        api.listRoles(),
        api.listPermissions(),
      ]);
      setAccounts(accountsRes || []);
      setRoles(rolesRes || []);
      setMatrix(matrixRes || []);
      // Tug list for assigning a captain / chief engineer login; optional.
      api.getDtrTugs().then((t) => setTugs(t || [])).catch(() => setTugs([]));
      if (rolesRes?.length && !selectedRoleId) {
        setSelectedRoleId(rolesRes[0].id);
      }
    } catch (err) {
      setLoadError(err.message || 'Failed to load RBAC data.');
    } finally {
      setLoading(false);
    }
  }, [selectedRoleId]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the selected role changes, hydrate the policy editor from the matrix.
  useEffect(() => {
    const row = matrix.find((m) => m.roleId === selectedRoleId);
    const next = {};
    for (const f of PERMISSION_FIELDS) next[f.key] = Boolean(row?.[f.key]);
    setPolicy(next);
  }, [selectedRoleId, matrix]);

  const editAccount = (account) => {
    setAccountMsg(null);
    setFormErrors({});
    setForm({
      id: account.id,
      name: account.name,
      email: account.email,
      status: account.status || 'Active',
      roleIds: (account.roles || []).map((r) => r.id),
      tugId: account.tug_id || '',
    });
  };

  const newAccount = () => {
    setAccountMsg(null);
    setFormErrors({});
    setForm(EMPTY_ACCOUNT);
  };

  const clearFormError = (key) =>
    setFormErrors((errs) => {
      if (!errs[key]) return errs;
      const rest = { ...errs };
      delete rest[key];
      return rest;
    });

  // Set one form field and clear its (now stale) validation message (8826).
  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    clearFormError(key);
  };

  const toggleFormRole = (roleId) => {
    clearFormError('roleIds');
    setForm((f) => {
      const has = f.roleIds.includes(roleId);
      return { ...f, roleIds: has ? f.roleIds.filter((id) => id !== roleId) : [...f.roleIds, roleId] };
    });
  };

  const validateForm = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'User full name is required';
    if (!form.email.trim()) next.email = 'Work email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email';
    if (!STATUSES.includes(form.status)) next.status = 'Select a status';
    if (form.roleIds.length === 0) next.roleIds = 'Assign at least one role';
    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const submitAccount = async (e) => {
    e.preventDefault();
    setAccountMsg(null);
    if (!validateForm()) return;
    setSavingAccount(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        status: form.status,
        roleIds: form.roleIds,
        // A cleared tug select is '' — send null so the link is removed.
        tugId: form.tugId || null,
      };
      if (form.id) {
        await api.updateAccount(form.id, payload);
        setAccountMsg({ type: 'success', text: 'Account changes applied successfully.' });
      } else {
        const created = await api.createAccount(payload);
        // The new login gets a temporary password: emailed when SMTP is set
        // up, and shown here outside production (8788).
        const parts = [`Account created for ${created?.email || payload.email}.`];
        parts.push(
          created?.emailSent
            ? 'Login details were emailed to the user.'
            : 'The login email could not be sent — hand the temporary password over directly.'
        );
        setAccountMsg({
          type: created?.emailSent ? 'success' : 'warning',
          text: parts.join(' '),
          tempPassword: created?.tempPassword,
        });
      }
      await loadAll();
      if (!form.id) setForm(EMPTY_ACCOUNT);
    } catch (err) {
      setAccountMsg({ type: 'danger', text: err.message || 'Failed to save account.' });
    } finally {
      setSavingAccount(false);
    }
  };

  const submitPolicy = async () => {
    setPolicyMsg(null);
    if (!selectedRoleId) return;
    setSavingPolicy(true);
    try {
      await api.updateRolePermission(selectedRoleId, policy);
      setPolicyMsg({ type: 'success', text: 'Access policy mappings saved.' });
      await loadAll();
    } catch (err) {
      setPolicyMsg({ type: 'danger', text: err.message || 'Failed to save policy.' });
    } finally {
      setSavingPolicy(false);
    }
  };

  const accountSearch = accountQuery.trim().toLowerCase();
  const filteredAccounts = accountSearch
    ? accounts.filter((acc) =>
        `${acc.name || ''} ${acc.email || ''}`.toLowerCase().includes(accountSearch)
      )
    : accounts;

  return (
    <div className="container-fluid py-4" data-testid="rbac-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-users-cog me-2" aria-hidden="true" />
                User Accounts, Roles &amp; Permissions (RBAC)
              </h3>
              <p className="text-muted mb-0 small">
                Govern platform user access privileges, mapping accounts to the 9 primary system roles.
              </p>
            </div>
            <span className="badge bg-primary p-2">Security: Active-RBAC</span>
          </div>
        </div>
      </div>

      {/* Functional separation advisory banner */}
      <div className="row mb-4">
        <div className="col-12">
          <div
            className="alert alert-info border-0 shadow-sm mb-0 d-flex align-items-start"
            role="alert"
            data-testid="rbac-separation-banner"
          >
            <i className="fas fa-info-circle fa-lg me-3 mt-1 text-info" aria-hidden="true" />
            <div>
              <h5 className="fw-bold text-dark mb-1">
                Functional Separation of SaaS Provider Billing &amp; Local System Access Policies
              </h5>
              <p className="text-secondary mb-0 small">
                To prevent billing errors, licensing conflicts, or compliance audit overlap, the
                platform maintains a <strong>strict functional separation</strong> between local user
                access mappings (RBAC) and SaaS software provider transactional licensing fees. This
                panel governs operator logins, personnel profiles, and feature-level security
                permissions only. Settings related to SaaS transaction metrics, billing rates, and
                license invoices are restricted to internal Software Provider Admins.
              </p>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" role="alert" data-testid="rbac-load-error">
          {loadError}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: account registry */}
          <div className="col-lg-7 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-users me-2" aria-hidden="true" />
                  Active Accounts
                </h5>
                <button className="btn btn-sm btn-primary" onClick={newAccount} data-testid="add-account">
                  <i className="fas fa-plus me-1" aria-hidden="true" />
                  Add Account
                </button>
              </div>
              <div className="card-body p-0">
                <div className="px-3 pt-3">
                  <div className="input-group input-group-sm mb-3">
                    <span className="input-group-text bg-white">
                      <i className="fas fa-search text-muted" aria-hidden="true" />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search accounts by name or email…"
                      value={accountQuery}
                      onChange={(e) => setAccountQuery(e.target.value)}
                      data-testid="rbac-account-search"
                      aria-label="Search accounts"
                    />
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>User Name</th>
                        <th>Work Email</th>
                        <th>Assigned Role</th>
                        <th>Status</th>
                        <th aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAccounts.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            {accounts.length === 0 ? 'No accounts yet.' : 'No accounts match your search.'}
                          </td>
                        </tr>
                      )}
                      {filteredAccounts.map((acc) => (
                        <tr key={acc.id} data-testid="account-row" data-email={acc.email}>
                          <td className="fw-bold text-dark">{acc.name}</td>
                          <td>
                            <code>{acc.email}</code>
                          </td>
                          <td>
                            {(acc.roles || []).length === 0 ? (
                              <span className="text-muted small">—</span>
                            ) : (
                              (acc.roles || []).map((r) => (
                                <span key={r.id} className="badge bg-light text-dark border me-1 mb-1">
                                  {r.name}
                                </span>
                              ))
                            )}
                          </td>
                          <td>
                            <span className={`badge ${statusBadge(acc.status)}`}>{acc.status}</span>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => editAccount(acc)}
                              data-testid="edit-account"
                              aria-label={`Edit ${acc.name}`}
                            >
                              <i className="fas fa-pen" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: manage account + policy matrix */}
          <div className="col-lg-5 mb-4">
            {/* Manage User Account */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-user-edit me-2 text-primary" aria-hidden="true" />
                  {form.id ? 'Manage User Account' : 'Add User Account'}
                </h5>
              </div>
              <div className="card-body">
                {accountMsg && (
                  <div className={`alert alert-${accountMsg.type}`} role="alert" data-testid="account-message">
                    {accountMsg.text}
                    {accountMsg.tempPassword && (
                      <div className="mt-1 small">
                        Temporary password:{' '}
                        <code className="user-select-all" data-testid="account-temp-password">
                          {accountMsg.tempPassword}
                        </code>
                      </div>
                    )}
                  </div>
                )}
                <form onSubmit={submitAccount} noValidate>
                  <div className="mb-3">
                    <label htmlFor="acc-name" className="form-label small fw-bold text-muted">
                      User Full Name
                    </label>
                    <input
                      id="acc-name"
                      type="text"
                      className={`form-control form-control-sm${formErrors.name ? ' is-invalid' : ''}`}
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      data-testid="account-name"
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="acc-email" className="form-label small fw-bold text-muted">
                      Work Email
                    </label>
                    <input
                      id="acc-email"
                      type="email"
                      className={`form-control form-control-sm${formErrors.email ? ' is-invalid' : ''}`}
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      data-testid="account-email"
                    />
                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="acc-status" className="form-label small fw-bold text-muted">
                      Status
                    </label>
                    <select
                      id="acc-status"
                      className={`form-select form-select-sm${formErrors.status ? ' is-invalid' : ''}`}
                      value={form.status}
                      onChange={(e) => setField('status', e.target.value)}
                      data-testid="account-status"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-bold text-muted d-block">
                      Role Mapping (one or more)
                    </label>
                    <div
                      className={`border rounded p-2${formErrors.roleIds ? ' border-danger' : ''}`}
                      style={{ maxHeight: 180, overflowY: 'auto' }}
                      data-testid="account-roles"
                    >
                      {roles.map((role) => (
                        <div className="form-check" key={role.id}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`role-${role.id}`}
                            checked={form.roleIds.includes(role.id)}
                            onChange={() => toggleFormRole(role.id)}
                            data-testid={`account-role-${role.id}`}
                          />
                          <label className="form-check-label small" htmlFor={`role-${role.id}`}>
                            {role.name}
                          </label>
                        </div>
                      ))}
                    </div>
                    {formErrors.roleIds && (
                      <div className="text-danger small mt-1">{formErrors.roleIds}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="account-tug" className="form-label small fw-bold text-muted">Assigned tug (captain / chief engineer)</label>
                    <select id="account-tug" className="form-select form-select-sm" value={form.tugId} onChange={(e) => setField('tugId', e.target.value)} data-testid="account-tug">
                      <option value="">None</option>
                      {tugs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-sm w-100"
                    disabled={savingAccount}
                    data-testid="apply-account-changes"
                  >
                    {savingAccount && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    <i className="fas fa-save me-1" aria-hidden="true" />
                    Apply Account Changes
                  </button>
                </form>
              </div>
            </div>

            {/* Security Policy Matrix */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-shield-alt me-2 text-warning" aria-hidden="true" />
                  Security Policy Matrix
                </h5>
              </div>
              <div className="card-body">
                {policyMsg && (
                  <div className={`alert alert-${policyMsg.type}`} role="alert" data-testid="policy-message">
                    {policyMsg.text}
                  </div>
                )}
                <div className="mb-3">
                  <label htmlFor="matrix-role" className="form-label small fw-bold text-muted">
                    Role Node
                  </label>
                  <select
                    id="matrix-role"
                    className="form-select form-select-sm"
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    data-testid="matrix-role"
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="form-label small fw-bold text-muted d-block">Access Controls</label>
                {PERMISSION_FIELDS.map((f) => (
                  <div className="form-check mb-2" key={f.key}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`perm-${f.key}`}
                      checked={Boolean(policy[f.key])}
                      onChange={(e) => setPolicy({ ...policy, [f.key]: e.target.checked })}
                      data-testid={`perm-${f.key}`}
                    />
                    <label className="form-check-label" htmlFor={`perm-${f.key}`}>
                      {f.label}
                    </label>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100 mt-3"
                  onClick={submitPolicy}
                  disabled={savingPolicy || !selectedRoleId}
                  data-testid="apply-access-changes"
                >
                  {savingPolicy && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  )}
                  <i className="fas fa-save me-1" aria-hidden="true" />
                  Apply Access Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
